<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { AppFlatSetting } from '@sancare/typescript-commons/src/settings/types'
import { HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'
import _ from 'lodash'
import { computed, ref } from 'vue'

import { predictionModelTypeKey, predictionModelTypeKeys } from '@/misc/prediction'
import { SuggestionJustification } from '@/misc/types'
import { useEvaluationFeedbackActions } from '@/stay-displayer/composables/useEvaluationFeedbackActions'
import { CodingSelection, Suggestion, SuggestionDiagnosis } from '@/stay-displayer/types'
import { useStore } from '@/store'
import { Diagnosis, PredictedLabelType } from '@/store/modules/diagnosis/types'
import { CountryEnum, ModeEnum } from '@/store/modules/settings/types'
import { StandardModeDiagnosisGroup } from '@/store/modules/stay/mco/types'
import { StayHealthData } from '@/store/modules/stay/types'

import JustificationSummary from '../ApiJustificationSummary.vue'
import ManualDiagnosisButton from '../ManualDiagnosisButton.vue'
import { hasRejectedSuggestion, isFirstRejectedSuggestion, isSuggestionDisplayed, showMoreLabelKey, sortRejectedSuggestionsLast, suggestionKey } from '../rejectedSuggestions'
import SuggestedDiagnosis from '../SuggestedDiagnosis.vue'
import SvgSearchIcon from '../SvgSearchIcon.vue'

export interface SingleSelectedItemListProps {
  rumId: number
  type: PredictedLabelType.DP | PredictedLabelType.DR
  suggestedDiagnoses: Suggestion[]
  standardModeDiagnosisGroup: StandardModeDiagnosisGroup
  healthData: StayHealthData
  selection?: CodingSelection | null
  isLoading?: boolean
  appSettings: AppFlatSetting
  suggestionJustifications?: Record<string, SuggestionJustification> | null
  mode: ModeEnum
}

const props = withDefaults(defineProps<SingleSelectedItemListProps>(), {
  selection: null,
  isLoading: false,
  suggestionJustifications: null,
})

const emit = defineEmits<{
  'make-selection': [selection: CodingSelection]
}>()

const store = useStore()

const showMore = ref(false)
const extendedSuggestions = ref<string[]>([])
const showJustificationSummary = ref(false)
const processingDiagnoses = ref<string[]>([])

const pmsiCountry = computed(() => store.state.settings.pmsi.country)
const aiEnabled = computed(() => store.getters.flatSancareSettings['enable_ai_suggestions'])
const stayEndDate = computed(() => store.state.stayDisplayer.displayedStay.stayEnd)

const firstDiagnosis = computed<Diagnosis | null>(() => {
  return props.standardModeDiagnosisGroup[`first${props.type}`]
})

const currentDiagnosis = computed<Diagnosis | null>(() => {
  return props.standardModeDiagnosisGroup[`current${props.type}`]
})
const currentDiagnosisOrigin = computed<string | null>(() => props.standardModeDiagnosisGroup[`current${props.type}Origin`])

const hidesRejected = computed(() => hasRejectedSuggestion(props.suggestedDiagnoses))

const displayedSuggestedDiagnoses = computed<Suggestion[]>(() => {
  return sortRejectedSuggestionsLast(props.suggestedDiagnoses.filter((suggestion) => isSuggestionDisplayed(suggestion, showMore.value)))
})

const codedSuggestion = computed<Suggestion | null>(() => {
  if (currentDiagnosis.value) {
    return props.suggestedDiagnoses.find((suggestion) => {
      if (!suggestion.isWildcard) {
        return suggestion.reference === currentDiagnosis.value.reference
      }
      return suggestion.diagnoses.some((diag: Diagnosis) => diag.reference === currentDiagnosis.value.reference)
    }) ?? null
  }
  return null
})

const suggestedDiagnosis = computed<SuggestionDiagnosis | null>(() => {
  return codedSuggestion.value?.diagnoses?.find((diag) => diag.reference === currentDiagnosis.value.reference) ?? null
})

const apiJustification = computed(() => {
  return props.suggestionJustifications?.[`${props.type}:${suggestedDiagnosis.value?.reference}`] ?? null
})

function readOnly(): boolean {
  return props.rumId === 0
}

function hasHiddenSuggestion(): boolean {
  if (hidesRejected.value) {
    return true
  }
  return props.suggestedDiagnoses.some((suggestion) => !suggestion.isAlwaysVisible && (suggestion.isOverThreshold || suggestion.searches.length))
}

async function addDiagnosis(suggestedDiag: Diagnosis) {
  if (processingDiagnoses.value.includes(suggestedDiag.reference)) {
    return
  }
  processingDiagnoses.value.push(suggestedDiag.reference)
  await store.dispatch('addDiagnosis', {
    rumId: props.rumId,
    diagnosisType: props.type,
    reference: suggestedDiag.reference,
    justifications: null,
  })
  _.remove(processingDiagnoses.value, (diag) => diag === suggestedDiag.reference)
}

function removeCurrentDiagnosis() {
  if (props.type === 'DP') {
    store.dispatch('removeDP', { rumId: props.rumId })
  } else if (props.type === 'DR') {
    store.dispatch('removeDR', { rumId: props.rumId })
  }
}

function makeSelection(selection: CodingSelection) {
  emit('make-selection', selection)
}

function switchToAssociatedDiagnosis(diagnosis: Diagnosis) {
  store.dispatch('switchDiagnosis', {
    rumId: props.rumId,
    origin: {
      diagnosisType: props.type,
      diagnosisReference: diagnosis.reference,
    },
    destination: 'DAS',
  })
}

function getSearchesIds(diagnosis: SuggestionDiagnosis): number[] {
  return diagnosis.staySavedSearches.map((savedSearch) => savedSearch.search.id)
}

function stringifySuggestionSearches(suggestion: SuggestionDiagnosis, sep: string, cut = false): string {
  const content = ([...suggestion.staySavedSearches].map((savedSearch) => savedSearch.search.name)).join(sep)
  if (cut) {
    return content.substring(0, 20)
  }
  return content
}

function isSelectedSuggestion(suggestion: SuggestionDiagnosis): boolean {
  const selectionIds = [...(props.selection?.savedSearchIds ?? [])].sort((a: number, b: number) => a - b)
  const suggestionIds = [...getSearchesIds(suggestion)].sort((a: number, b: number) => a - b)

  return Boolean(
    props.selection
    && props.selection.labelType === props.type
    && props.selection.labelReference === suggestion.reference
    && _.isEqual(selectionIds, suggestionIds)
  )
}

function isExtendedSuggestion(suggestion: Suggestion): boolean {
  return extendedSuggestions.value.includes(suggestionKey(suggestion))
}

function toggleSuggestionDiagnoses(suggestion: Suggestion) {
  if (suggestion.isWildcard) {
    if (isExtendedSuggestion(suggestion)) {
      extendedSuggestions.value = extendedSuggestions.value.filter((key) => key !== suggestionKey(suggestion))
    } else {
      extendedSuggestions.value.push(suggestionKey(suggestion))
    }
  }
}

function hasRevaloSearch(suggestion: SuggestionDiagnosis): boolean {
  return suggestion.staySavedSearches.some((sss) => sss.search.type === ModeEnum.OPTIMIZABLE)
}

function toggleRationals() {
  this.showJustificationSummary = !this.showJustificationSummary
  if (this.showJustificationSummary) {
    makeSelection({
      labelType: this.type,
      labelReference: this.suggestedDiagnosis.reference,
      savedSearchIds: this.suggestedDiagnosis.staySavedSearches.map((ss) => ss.search.id),
    })
  }
}

function handleHealthDataSelection(mainContent: HealthDataDocument, scrollTarget: { docId: number, offset: number } | null = null) {
  store.commit('setScrollTarget', scrollTarget)
  store.commit('setMainContent', mainContent)
  makeSelection({
    labelType: props.type,
    labelReference: suggestedDiagnosis.value?.reference,
    savedSearchIds: suggestedDiagnosis.value?.staySavedSearches.map((ss) => ss.search.id),
  })
  showJustificationSummary.value = !showJustificationSummary.value
}

const evaluationFeedbackActions = useEvaluationFeedbackActions()

function flagJustification(payload) {
  store.dispatch('createFalseJustificationForSuggestion', payload)
}

function unflagJustification({ justificationId, docType, docId }) {
  store.dispatch('deleteFalseJustification', {
    justificationId,
    reportId: docType === 'report' ? docId : null,
    healthEntryId: docType === 'textualHealthEntry' ? docId : null,
  })
}
</script>

<template>
  <div class="row g-0 gap-3 align-items-start">
    <div class="col">
      <div class="card diagnosis-card highlighted">
        <div class="card-body">
          <h1 class="card-title d-flex align-items-center">
            <span>{{ $t('diagnosis.'+type) }}</span>
            <small class="ms-1 fs-6">- {{ $t('stayDisplayer.mco.diagnosisStatus.current') }}</small>
          </h1>
        </div>
        <div
          v-if="firstDiagnosis && currentDiagnosis && currentDiagnosis.reference === firstDiagnosis.reference"
          class="card-body"
        >
          <div class="row g-0 align-items-center">
            <div class="col">
              <h2 :title="firstDiagnosis.description">
                {{ firstDiagnosis.reference }}
              </h2>
              <div class="row g-0">
                {{ firstDiagnosis.description }}
              </div>
              <div
                v-if="type === 'DP' && !readOnly()"
                class="row g-0"
              >
                <div
                  class="badge text-bg-primary col-auto"
                  @click="switchToAssociatedDiagnosis(firstDiagnosis)"
                >
                  <sancare-octicon
                    name="arrow-down"
                    :width="10"
                    :height="10"
                  />
                  <span class="cp">
                    {{ $t('diagnosis.DAS') }}
                  </span>
                </div>
              </div>
            </div>
            <div
              v-if="type === 'DR' && !readOnly()"
              title="Supprimer le diagnostic"
              class="col-auto predicted-diagnosis-remover"
              @click.stop="removeCurrentDiagnosis()"
            >
              <sancare-octicon
                name="x"
                :width="18"
                :height="18"
              />
            </div>
          </div>
        </div>
        <div
          v-else-if="firstDiagnosis && (!currentDiagnosis || firstDiagnosis.reference !== currentDiagnosis.reference)"
          class="card-body"
        >
          <s :title="firstDiagnosis.description">
            {{ firstDiagnosis.reference }}
          </s> (initial)
          <h2 v-if="!currentDiagnosis">
            Pas de {{ $t('diagnosis.'+type) }}
          </h2>
        </div>
        <div
          v-if="currentDiagnosis && (!firstDiagnosis || firstDiagnosis.reference !== currentDiagnosis.reference)"
          class="card-body"
        >
          <div class="row g-0 align-items-center">
            <div class="col">
              <div
                class="row"
                :title="currentDiagnosis.description"
              >
                <h2 class="col-auto">
                  {{ currentDiagnosis.reference }}
                </h2>
                <div
                  v-if="codedSuggestion"
                  title="Voir les justifications"
                  class="col-auto show-justifications"
                  @click.stop="toggleRationals"
                >
                  <v-dropdown
                    :shown="showJustificationSummary"
                    placement="left"
                    class="popover-container"
                    popover-class="justification-popover"
                  >
                    <slot />
                    <template #popper>
                      <justification-summary
                        :is-loading="isLoading"
                        :justification="suggestedDiagnosis.canShowRational ? apiJustification : null"
                        :health-data="healthData"
                        :label-type="type"
                        :label-reference="suggestedDiagnosis.reference"
                        :feedback-actions="evaluationFeedbackActions"
                        @select-health-data="handleHealthDataSelection"
                        @flag-justification="flagJustification"
                        @unflag-justification="unflagJustification"
                      />
                    </template>
                  </v-dropdown>
                  <div
                    v-if="suggestedDiagnosis && suggestedDiagnosis.canShowRational && !codedSuggestion.isWildcard && !readOnly()"
                    :class="{ selected: isSelectedSuggestion(suggestedDiagnosis) }"
                    class="btn-note icon-block"
                  >
                    <sancare-octicon
                      name="note"
                      :width="18"
                      :height="18"
                    />
                  </div>
                </div>
              </div>
              <div>
                {{ currentDiagnosis.description }}
              </div>
              <div
                v-if="!readOnly()"
                class="badge text-bg-primary col-auto"
                @click="switchToAssociatedDiagnosis(currentDiagnosis)"
              >
                <sancare-octicon
                  name="arrow-down"
                  :width="10"
                  :height="10"
                />
                <span class="cp">
                  {{ $t('diagnosis.DAS') }}
                </span>
              </div>
            </div>
            <div
              v-if="['prediction_default', 'prediction_manual', 'prediction_unsure'].includes(currentDiagnosisOrigin) && !readOnly()"
              title="Retirer le diagnotic"
              class="col-auto predicted-diagnosis-remover"
              @click.stop="removeCurrentDiagnosis()"
            >
              <sancare-octicon
                name="arrow-right"
                :width="18"
                :height="18"
              />
            </div>
            <div
              v-else-if="!readOnly()"
              title="Supprimer le diagnotic"
              class="col-auto predicted-diagnosis-remover"
              @click.stop="removeCurrentDiagnosis()"
            >
              <sancare-octicon
                name="x"
                :width="18"
                :height="18"
              />
            </div>
          </div>
        </div>
        <div
          v-if="!firstDiagnosis && !currentDiagnosis"
          class="card-body"
        >
          <h2>
            Pas de {{ $t('diagnosis.'+type) }}
          </h2>
        </div>
      </div>
      <div class="row g-0">
        <manual-diagnosis-button
          :type="type"
          :context-date="stayEndDate"
          :read-only="readOnly()"
          :can-add-uncommon-diagnoses="pmsiCountry === CountryEnum.FR"
          @add-diagnosis="ref => { addDiagnosis({ reference: ref }); }"
        />
      </div>
    </div>
    <div class="card diagnosis-card diagnosis-suggestions-card col">
      <div class="card-body px-0 pt-0">
        <h1 class="card-title">
          {{ $t('diagnosis.'+type) }} <small class="fs-6">- {{ $t('stayDisplayer.sancareSuggestion', displayedSuggestedDiagnoses.length) }}</small>
        </h1>
      </div>
      <div>
        <div
          v-for="(suggestion, suggestionIdx) in displayedSuggestedDiagnoses"
          :key="`suggestion_${type}_${suggestionKey(suggestion)}`"
        >
          <div
            v-if="isFirstRejectedSuggestion(displayedSuggestedDiagnoses, suggestionIdx)"
            class="rejected-suggestions-separator"
          />
          <div class="row card-body px-0">
            <suggested-diagnosis
              :app-settings="appSettings"
              :type="type"
              :is-loading="isLoading"
              :is-wildcard="suggestion.isWildcard"
              :selected="selection !== null && selection.labelType === type && selection.labelReference === suggestion.reference"
              :suggested-diagnosis="suggestion.diagnoses[0]"
              :suggestion-justifications="suggestionJustifications"
              :health-data="healthData"
              :ia-enabled="aiEnabled"
              class="col-12"
              @make-selection="makeSelection"
            >
              <div
                class="row g-0 align-items-center diagnosis-item"
                @click="toggleSuggestionDiagnoses(suggestion)"
              >
                <div class="col-2">
                  <div
                    v-if="suggestion.isWildcard"
                    title="Voir les diagnostics correspondants"
                    class="predicted-diagnosis-adder"
                  >
                    <sancare-octicon
                      :name="isExtendedSuggestion(suggestion) ? 'diff-removed' : 'diff-added'"
                      :width="18"
                      :height="18"
                    />
                  </div>
                  <div
                    v-else-if="(!currentDiagnosis || currentDiagnosis.reference !== suggestion.reference) && !readOnly()"
                    title="Ajouter le diagnostic"
                    class="predicted-diagnosis-adder"
                    @click.stop="addDiagnosis(suggestion.diagnoses[0])"
                  >
                    <div
                      v-if="processingDiagnoses.includes(suggestion.diagnoses[0].reference)"
                      class="loader loader-sm"
                    />
                    <sancare-octicon
                      v-else
                      name="arrow-left"
                      :width="20"
                      :height="20"
                    />
                  </div>
                </div>
                <div class="col-9 row g-0 align-items-center">
                  <div
                    :data-reference="suggestion.reference"
                    :title="suggestion.description"
                    class="col-5"
                  >
                    <h2 class="diagnosis-reference">
                      {{ suggestion.reference }}
                    </h2>
                  </div>
                  <div class="col-6">
                    <div
                      v-if="suggestion.searches.length"
                      v-tooltip="stringifySuggestionSearches(suggestion.diagnoses[0], ',\n')"
                      class="row g-0 align-items-center"
                    >
                      <div class="col-3">
                        <svg-search-icon
                          :is-revalo="hasRevaloSearch(suggestion.diagnoses[0])"
                        />
                      </div>
                      <div class="col-8 ms-1 diagnosis-searches">
                        {{ stringifySuggestionSearches(suggestion.diagnoses[0], ', ', true) }}
                      </div>
                    </div>
                    <div
                      v-for="modelKey in predictionModelTypeKeys(suggestion.predictions)"
                      :key="modelKey"
                      class="row g-0 align-items-center"
                      :class="{ 'ia-disabled-line': !aiEnabled }"
                    >
                      <div class="col-3">
                        <sancare-octicon
                          name="robot"
                          :width="18"
                        />
                      </div>
                      <div
                        v-tooltip="$t(modelKey)"
                        class="col-8 ms-1 diagnosis-confidence text-truncate"
                      >
                        {{ $t(modelKey) }}
                      </div>
                    </div>
                  </div>
                  <div class="col-1">
                    <div
                      v-if="suggestion.diagnoses[0] && suggestion.diagnoses[0].canShowRational && !suggestion.isWildcard"
                      :class="{ selected: isSelectedSuggestion(suggestion.diagnoses[0]) }"
                      class="btn-select-diagnosis icon-block"
                      @click.stop="makeSelection({ labelType: type, labelReference: suggestion.reference, savedSearchIds: getSearchesIds(suggestion.diagnoses[0]) })"
                    >
                      <sancare-octicon
                        name="note"
                        :width="18"
                        :height="18"
                      />
                    </div>
                  </div>
                </div>
                <div
                  v-if="suggestion.hasRejectedEvaluation"
                  class="col-1 d-flex align-items-center"
                >
                  <sancare-octicon
                    name="blocked"
                    class="rejected-suggestion-icon"
                    :tooltip-text="$t('stayDisplayer.rejectedSuggestion')"
                    :width="16"
                    :height="16"
                  />
                </div>
              </div>
              <div
                v-if="suggestionIdx === 0"
                class="row g-0 align-items-center"
              >
                <p>
                  {{ suggestion.description }}
                </p>
              </div>
            </suggested-diagnosis>
          </div>
          <div v-if="suggestion.isWildcard && isExtendedSuggestion(suggestion) && suggestion.diagnoses.length > 1">
            <div
              v-for="diagnosis in suggestion.diagnoses"
              :key="`suggested_diagnosis_${type}_${diagnosis.reference}`"
              class="row card-body px-0"
            >
              <suggested-diagnosis
                :app-settings="appSettings"
                :type="type"
                :is-loading="isLoading"
                :is-wildcard="false"
                :selected="selection !== null && selection.labelType === type && selection.labelReference === diagnosis.reference"
                :suggested-diagnosis="diagnosis"
                :suggestion-justifications="suggestionJustifications"
                :health-data="healthData"
                :ia-enabled="aiEnabled"
                class="col-12"
                @make-selection="makeSelection"
              >
                <div class="row g-0 align-items-center diagnosis-item">
                  <!-- @click="/toggleDiagCategory(sDiag)" -->
                  <div class="col-2">
                    <div
                      v-if="(!currentDiagnosis || currentDiagnosis.reference !== diagnosis.reference) && !readOnly()"
                      title="Ajouter le diagnostic"
                      class="predicted-diagnosis-adder"
                      @click.stop="addDiagnosis(diagnosis)"
                    >
                      <div
                        v-if="processingDiagnoses.indexOf(diagnosis.reference) !== -1"
                        class="loader loader-sm"
                      />
                      <sancare-octicon
                        v-else
                        name="arrow-left"
                        :width="20"
                        :height="20"
                      />
                    </div>
                  </div>
                  <div class="col-9 row g-0 align-items-center">
                    <div
                      :data-reference="diagnosis.reference"
                      :title="diagnosis.description"
                      class="col-5"
                    >
                      <h2 class="diagnosis-reference">
                        {{ diagnosis.reference }}
                      </h2>
                    </div>
                    <div class="col-6">
                      <div
                        v-if="diagnosis.staySavedSearches.length"
                        v-tooltip="stringifySuggestionSearches(diagnosis, ',\n')"
                        class="row g-0 align-items-center"
                      >
                        <div class="col-3">
                          <svg-search-icon
                            :is-revalo="hasRevaloSearch(diagnosis)"
                          />
                        </div>
                        <div class="col-8 ms-1 diagnosis-searches">
                          {{ stringifySuggestionSearches(diagnosis, ', ', true) }}
                        </div>
                      </div>
                      <div
                        v-if="diagnosis.prediction"
                        class="row g-0 align-items-center"
                        :class="{ 'ia-disabled-line': !aiEnabled }"
                      >
                        <div class="col-3">
                          <sancare-octicon
                            name="robot"
                            :width="18"
                          />
                        </div>
                        <div
                          v-if="predictionModelTypeKey(diagnosis.prediction)"
                          v-tooltip="$t(predictionModelTypeKey(diagnosis.prediction))"
                          class="col-8 ms-1 diagnosis-confidence text-truncate"
                        >
                          {{ $t(predictionModelTypeKey(diagnosis.prediction)) }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-1">
                    <div
                      v-if="diagnosis.canShowRational"
                      :class="{ selected: isSelectedSuggestion(diagnosis) }"
                      class="btn-select-diagnosis icon-block"
                      @click.stop="makeSelection({ labelType: type, labelReference: diagnosis.reference, savedSearchIds: getSearchesIds(diagnosis) })"
                    >
                      <sancare-octicon
                        name="note"
                        :width="18"
                        :height="18"
                      />
                    </div>
                  </div>
                </div>
              </suggested-diagnosis>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="isLoading && !suggestedDiagnoses.length"
        class="loader loader-lg loader-center"
      />
      <div
        v-else-if="!suggestedDiagnoses.length"
        class="card-body px-0 text"
      >
        Aucun diagnostic detecté
      </div>
      <div
        v-else-if="displayedSuggestedDiagnoses.length === 0"
        class="card-body text-center"
      >
        Aucun diagnostic pertinent
      </div>
      <div
        v-if="hasHiddenSuggestion()"
        class="col-12 mb-1 py-0 text-center"
      >
        <span
          :id="'show-more-'+type"
          class="btn-more-diagnosis"
          @click="showMore = !showMore"
        >
          {{ $t(showMoreLabelKey(hidesRejected, showMore)) }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-title {
  font-size: 2rem;
}

h2 {
  font-size: 1.1rem;
}
</style>
